import { FalhaDaApi, textoDoErro } from "@/api/problema";

/*
 * As recusas do envio, em português nosso.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A TELA FALA POR CÓDIGO, E NUNCA PELO TEXTO CRU.                          │
 * │                                                                          │
 * │ Reprocessar é o gesto mais perigoso do painel, e cada recusa tem de      │
 * │ dizer o que aconteceu e o que fazer. `detail` é o mesmo texto do log,    │
 * │ em minúscula. Código novo cai na frase genérica, e `recusas.test.ts`     │
 * │ confere o catálogo: operador nenhum lê `boleto_fora_de_falha`.           │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
const FRASES: Readonly<Record<string, string>> = {
  boleto_fora_de_falha: "O boleto saiu de FALHA. Confira a situação dele.",
  boleto_com_titulo:
    "O boleto tem título no provider. Emitir de novo seria uma segunda cobrança.",
  boleto_resolvido_manualmente: "Um operador já resolveu este boleto, e a decisão fica.",
  envio_ja_foi_incerto:
    "Um envio deste boleto ficou incerto. O título daquela tentativa pode estar vivo no provider.",
  estado_do_envio_nao_reprocessavel:
    "O envio está em aberto, ou o título achado não é deste boleto.",
  conflito_de_nosso_numero:
    "O nosso número é de um título que existe no provider. A decisão é humana.",
  falha_de_dado: "O provider recusou o dado do boleto. Reprocessar daria a mesma recusa.",
  falha_sem_classe:
    "A falha não tem classe registrada, e não dá para saber se existe título vivo.",
  cancelamento_em_aberto: "Há pedido de cancelamento em aberto. Aguarde o desfecho dele.",
  titulo_encontrado:
    "A sonda achou título para este boleto. Trate a divergência em Operação antes de reprocessar.",
  possivel_duplicidade: "Apareceu boleto parecido. Confira a lista antes de confirmar.",
  parecidos_demais: "A lista passou de dez boletos parecidos. Investigue antes de reprocessar.",
  sonda_necessaria: "A sonda venceu. Procure o título de novo antes de reprocessar.",
  reprocessamento_nao_concluido:
    "O boleto mudou durante a ação, e nada foi gravado. Confira o estado dele.",
  verificacao_em_andamento: "O conciliador está consultando este boleto. Tente em instantes.",
  boleto_nao_sondavel: "A sonda não vale para este boleto no estado atual.",
  sonda_recente: "A sonda já foi pedida, e o resultado aparece aqui.",
  orfao_ja_tratado: "Outro operador já tratou este título.",
  justificativa_ausente: "Escreva a justificativa, com pelo menos 10 caracteres.",
  justificativa_longa: "A justificativa passou de 500 caracteres.",
};

/*
 * O gate que o detalhe devolve fala de outra situação que a recusa de mesmo código: a recusa é
 * de quem confirmou com a lista velha, e o gate é de quem não tem como conferir tudo.
 */
const FRASES_DO_GATE: Readonly<Record<string, string>> = {
  possivel_duplicidade:
    "Há mais boletos parecidos do que a lista mostra. Investigue antes de reprocessar.",
};

/** Conflito de código que o catálogo não conhece: a frase não inventa a causa. */
const CONFLITO_DESCONHECIDO = "O estado do boleto impediu a ação. Confira o boleto.";

/** `hasOwn`: o código vem da rede, e `constructor` não pode virar frase. */
function fraseDoCodigo(codigo: string | null): string | undefined {
  return codigo !== null && Object.hasOwn(FRASES, codigo) ? FRASES[codigo] : undefined;
}

/** `100` vira `1 min 40 s`: é o tempo que o operador vai esperar, e não um número de cabeçalho. */
export function esperaLegivel(segundos: number): string {
  const minutos = Math.floor(segundos / 60);
  const resto = segundos % 60;
  if (minutos === 0) return `${resto} s`;
  return resto === 0 ? `${minutos} min` : `${minutos} min ${resto} s`;
}

/** A frase do gate que o detalhe do boleto devolve. Só sai frase nossa. */
export function fraseDoGate(codigo: string): string {
  if (Object.hasOwn(FRASES_DO_GATE, codigo)) return FRASES_DO_GATE[codigo] as string;
  return fraseDoCodigo(codigo) ?? CONFLITO_DESCONHECIDO;
}

/** A frase de uma recusa. O que não é recusa da API segue o `textoDoErro` de sempre. */
export function fraseDaRecusa(falha: unknown): string {
  if (!(falha instanceof FalhaDaApi)) return textoDoErro(falha);

  const frase = fraseDoCodigo(falha.codigo);
  if (frase !== undefined) {
    return falha.tenteEmSegundos === null
      ? frase
      : `${frase} Peça de novo em ${esperaLegivel(falha.tenteEmSegundos)}.`;
  }
  if (falha.status === 409) return CONFLITO_DESCONHECIDO;

  const texto = textoDoErro(falha);
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** Os segundos que a recusa pede para esperar, só em `sonda_recente`. */
export function esperaDaRecusa(falha: unknown): number | null {
  return falha instanceof FalhaDaApi && falha.codigo === "sonda_recente"
    ? falha.tenteEmSegundos
    : null;
}
